// TREATX-MULTICCY-P0 — settlement accounts & currency capability.
// Reads one projection (/api/clinic/settlement-accounts) that joins the F1
// bank accounts, the F5 payment_accounts lifecycle and the PSP capability
// registry. Nothing is inferred client-side and no optimistic "ready to accept
// EUR" claim is ever rendered.
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { Landmark, RefreshCw, CheckCircle2, Clock, Lock, AlertTriangle } from 'lucide-react';
import api from '../../utils/api';

const PSP_STATUS_KEY = {
  not_registered: 'pspNotRegistered',
  link_unresolved: 'pspLinkUnresolved',
  initiated: 'pspInitiated',
  in_review: 'pspInReview',
  active: 'pspActive',
  restricted: 'pspRestricted',
  closed: 'pspClosed',
};

const Fact = ({ ok, children, testId }) => (
  <div className="flex items-start gap-1.5 text-[12px]" data-testid={testId}>
    {ok
      ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 mt-[1px] shrink-0" />
      : <Clock className="h-3.5 w-3.5 text-slate-400 mt-[1px] shrink-0" />}
    <span className={ok ? 'text-slate-700' : 'text-slate-500'}>{children}</span>
  </div>
);

export const SettlementAccountsCard = ({ refreshKey }) => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setFailed(false);
    try {
      const res = await api.get('/clinic/settlement-accounts');
      setData(res.data);
      // A notice from an earlier attempt describes a state that has just been
      // re-read. Keeping it on screen after a successful refresh is how a
      // stale "missing information" message outlived the thing it described.
      setBlocked({});
    } catch {
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load, refreshKey]);

  const deactivate = async (id) => {
    setBusy(id);
    try {
      await api.post(`/clinic/bank-accounts/${id}/deactivate`);
      toast.success(t('clinic.settlementAccounts.deactivated'));
      await load();
    } catch (e) {
      const code = e?.response?.data?.detail?.code || e?.response?.data?.code;
      const key = {
        SETTLEMENT_DESTINATION_IN_USE: 'deactivateBlocked',
        SETTLEMENT_DESTINATION_CLOSE_IN_PROGRESS: 'deactivateRetry',
        SETTLEMENT_COORDINATION_UNAVAILABLE: 'deactivateRetry',
        SETTLEMENT_DEACTIVATION_REVERTED: 'deactivateRetry',
        SETTLEMENT_DEACTIVATION_NEEDS_INTERVENTION: 'deactivateNeedsCheck',
        SETTLEMENT_DEACTIVATION_SUPERSEDED: 'deactivateNeedsCheck',
        SETTLEMENT_DEACTIVATION_RECOVERY_INCOMPLETE: 'deactivateRecoveryIncomplete',
      }[code] || 'loadFailed';
      toast.error(t(`clinic.settlementAccounts.${key}`));
      if (code) await load();
    } finally {
      setBusy(null);
    }
  };

  // PSP registration for ONE explicitly selected account.
  //
  // STATE TAXONOMY — the backend names the state; the UI never guesses. Only
  // PSP_REJECTED and PSP_RESULT_UNCERTAIN mean iyzico was actually reached, so
  // only those may show provider evidence. Everything else stopped inside
  // TreatX and must say so.
  const [blocked, setBlocked] = useState({});

  const STATE_TITLES = {
    LOCAL_VALIDATION_BLOCKED: 'pspStateLocalValidation',
    BANK_VERIFICATION_PENDING: 'pspStateBankVerification',
    INDEX_MIGRATION_REQUIRED: 'pspStateIndexMigration',
    CONFIGURATION_BLOCKED: 'pspStateConfiguration',
    PSP_REJECTED: 'pspStatePspRejected',
    // A provider SYSTEM error (documented codes 1/2) is not a decision about
    // this application, and an answer with no code at all is not classifiable.
    PSP_TEMPORARY_FAILURE: 'pspStateTemporaryFailure',
    PSP_RESULT_UNCERTAIN: 'pspStateUncertain',
  };

  const deriveState = (detail) => {
    if (detail.state) return detail.state;
    // Older deployments answer with codes only.
    return {
      psp_rejected_registration: 'PSP_REJECTED',
      psp_submerchant_bound_to_other_account: 'PSP_REJECTED',
      psp_temporary_failure: 'PSP_TEMPORARY_FAILURE',
      psp_result_unclassified: 'PSP_RESULT_UNCERTAIN',
      existence_unverified: 'PSP_RESULT_UNCERTAIN',
      index_migration_required: 'INDEX_MIGRATION_REQUIRED',
      bank_account_not_found: 'LOCAL_VALIDATION_BLOCKED',
      bank_account_not_ready: 'LOCAL_VALIDATION_BLOCKED',
      second_registration_requires_sandbox: 'CONFIGURATION_BLOCKED',
      configuration_blocked: 'CONFIGURATION_BLOCKED',
      iyzico_not_configured: 'CONFIGURATION_BLOCKED',
      requirements_outstanding: 'CONFIGURATION_BLOCKED',
    }[detail.code] || 'LOCAL_VALIDATION_BLOCKED';
  };

  // TREATX-IYZICO-SETTLEMENT-SCOPE-01 — which converted routes this screen may
  // offer. Read from the canonical capability matrix (`conversion.verified_pairs`)
  // instead of a hardcoded currency list, so a EUR→TRY action never silently
  // carries USD or GBP acceptance with it.
  const convertedPairsFor = (a) => (matrix?.conversion?.verified_pairs || [])
    .filter((p) => p.selectable
      && String(p.settlement_currency || '').toUpperCase()
        === String(a?.psp?.settlement_currency || '').toUpperCase())
    .map((p) => ({
      charge_currency: String(p.charge_currency || '').toUpperCase(),
      settlement_currency: String(p.settlement_currency || '').toUpperCase(),
    }));

  const acceptedPairsOf = (a) => (a?.psp?.converted_settlement_accepted_pairs || [])
    .map((p) => `${p.charge_currency}>${p.settlement_currency}`);

  // Pair-exact: "some accepted pair exists" must not hide a still-required
  // EUR→TRY acceptance.
  const missingPairsFor = (a) => {
    const have = new Set(acceptedPairsOf(a));
    return convertedPairsFor(a)
      .filter((p) => !have.has(`${p.charge_currency}>${p.settlement_currency}`));
  };

  const pairLabel = (pairs) => pairs
    .map((p) => `${p.charge_currency} → ${p.settlement_currency}`).join(', ');

  // Which currencies iyzico can actually SETTLE into, from the canonical
  // capability matrix. Scoped to this PSP: other PSPs may settle elsewhere.
  const iyzicoSettlementCurrencies = () => (matrix?.settlement || [])
    .filter((c) => c.selectable)
    .map((c) => String(c.currency || '').toUpperCase());

  // Tri-state on purpose. REVIEW FINDING A — "we have no capability evidence"
  // is NOT "this currency is supported": offering an iyzico registration with
  // no matrix at all is exactly how the owner was sent to register a EUR
  // settlement account iyzico cannot complete.
  //   true  → iyzico settles this currency
  //   false → iyzico does not settle this currency
  //   null  → capability data unavailable; nothing may be offered or refused
  const iyzicoSettles = (currency) => {
    if (!matrix || !Array.isArray(matrix.settlement)) return null;
    const settles = iyzicoSettlementCurrencies();
    if (!settles.length) return null;
    return settles.includes(String(currency || '').toUpperCase());
  };

  // Every fact the backend reports about money still moving to this account.
  // The button and the explanation must agree: unattributable or unverifiable
  // both keep switching off blocked, exactly as the server enforces.
  const deactivationBlocked = (a) => (a.in_flight_payments > 0)
    || (a.in_flight_unattributed > 0)
    || a.in_flight_verified === false;

  const acceptConverted = async (a) => {
    // Policy only: this records the provider's choice. It cannot make a route
    // work — the backend still requires the conversion capability to be
    // verified, and says so in its own response.
    const pairs = missingPairsFor(a);
    if (!pairs.length) return;
    setBusy(`acc-${a.bank_account_id}`);
    try {
      await api.post('/clinic/settlement-accounts/converted-settlement-acceptance', {
        payment_account_id: a.psp.payment_account_id,
        accepted_pairs: pairs,
        disclosure_version: 'converted-settlement-v1',
      });
      toast.success(t('clinic.settlementAccounts.convertedAcceptedToast'));
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message
        || t('clinic.settlementAccounts.loadFailed'));
    } finally {
      setBusy(null);
    }
  };

  const registerWithPsp = async (id) => {
    setBusy(id);
    try {
      const res = await api.post(`/clinic/iyzico/bank-accounts/${id}/register`);
      setBlocked((b) => ({ ...b, [id]: null }));
      toast.success(t(
        res?.data?.state === 'ALREADY_REGISTERED'
          ? 'clinic.settlementAccounts.pspAlreadyRegistered'
          : 'clinic.settlementAccounts.pspRegisterSubmitted',
        { currency: res?.data?.currency || '' },
      ));
      await load();
    } catch (e) {
      const detail = e?.response?.data?.detail || {};
      const state = deriveState(detail);
      setBlocked((b) => ({ ...b, [id]: { ...detail, state } }));
      if (state === 'PSP_REJECTED') {
        // iyzico ANSWERED and refused. Show its own code so the owner can take
        // it to the provider — this is the evidence the whole experiment is for.
        toast.error(t('clinic.settlementAccounts.pspRegisterRejected', {
          code: detail.provider_error_code || '—',
        }));
      } else {
        toast.error(t(`clinic.settlementAccounts.${
          { BANK_VERIFICATION_PENDING: 'pspRegisterNotVerified',
            INDEX_MIGRATION_REQUIRED: 'pspRegisterEnvNotReady',
            CONFIGURATION_BLOCKED: 'pspRegisterEnvNotReady',
            PSP_TEMPORARY_FAILURE: 'pspRegisterTemporaryFailure',
            PSP_RESULT_UNCERTAIN: 'pspRegisterUncertain',
          }[state] || 'pspRegisterNotReady'}`));
      }
    } finally {
      setBusy(null);
    }
  };

  const matrix = data?.currency_capabilities?.iyzico;

  return (
    <Card className="border border-slate-200/60 shadow-sm" data-testid="settlement-accounts-card">
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-3 mb-1">
          <div className="flex items-center gap-2">
            <Landmark className="h-4 w-4 text-slate-500" />
            <h3 className="text-sm font-semibold text-[#122033]" data-testid="settlement-accounts-title">
              {t('clinic.settlementAccounts.title')}
            </h3>
          </div>
          <Button variant="ghost" size="sm" onClick={load} data-testid="settlement-accounts-refresh">
            <RefreshCw className="h-3.5 w-3.5 mr-1" />{t('clinic.settlementAccounts.refresh')}
          </Button>
        </div>
        <p className="text-[12px] text-slate-500 mb-4">{t('clinic.settlementAccounts.subtitle')}</p>

        {loading && <div className="text-[12px] text-slate-500" data-testid="settlement-accounts-loading">…</div>}
        {failed && (
          <div className="text-[12px] text-rose-600" data-testid="settlement-accounts-error">
            {t('clinic.settlementAccounts.loadFailed')}
          </div>
        )}

        {!loading && !failed && (data?.accounts || []).length === 0 && (
          <p className="text-[12px] text-slate-500" data-testid="settlement-accounts-empty">
            {t('clinic.settlementAccounts.noAccounts')}
          </p>
        )}

        {!loading && !failed && (data?.accounts || []).map((a) => {
          const cap = a.capability || {};
          const statusKey = PSP_STATUS_KEY[a.psp_account_status] || 'pspNotRegistered';
          // OWNER DECISION 2026-10 — this panel is the iyzico settlement
          // journey. An account in a currency iyzico cannot settle is kept as
          // a bank record (and stays editable in Bank Accounts), but it is not
          // presented here as an iyzico destination with its own capability
          // facts, connect action and provider errors.
          const settles = iyzicoSettles(a.currency);
          if (settles === false) {
            return (
              <div
                key={a.bank_account_id}
                className="rounded-lg border border-dashed border-slate-200 p-3 mb-3"
                data-testid={`settlement-account-${a.bank_account_id}`}
              >
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-[13px] font-semibold text-slate-700" data-testid={`settlement-currency-${a.bank_account_id}`}>
                    {a.currency || '—'}
                  </span>
                  <span className="text-[12px] text-slate-500 font-mono">{a.iban_masked || '—'}</span>
                  {a.is_primary && <Badge variant="secondary" className="text-[10px]">{t('clinic.settlementAccounts.primary')}</Badge>}
                </div>
                <p
                  className="mt-1.5 text-[12px] text-slate-600"
                  data-testid={`settlement-psp-not-a-destination-${a.bank_account_id}`}
                >
                  {t('clinic.settlementAccounts.pspNotASettlementCurrency', {
                    currency: a.currency,
                    settles: iyzicoSettlementCurrencies().join(', '),
                  })}
                </p>
                {/* Money still moving to this bank account is a fact about the
                    account, not about the provider journey — it stays visible
                    even though the registration surface does not apply here. */}
                {a.in_flight_payments > 0 && (
                  <div className="mt-1.5 flex items-start gap-1.5 text-[12px] text-amber-700" data-testid={`fact-inflight-${a.bank_account_id}`}>
                    <Lock className="h-3.5 w-3.5 mt-[1px] shrink-0" />
                    <span>{t('clinic.settlementAccounts.inFlight', { count: a.in_flight_payments })}</span>
                  </div>
                )}
                {(a.in_flight_unattributed > 0 || a.in_flight_verified === false) && (
                  <div className="mt-1.5 flex items-start gap-1.5 text-[12px] text-amber-700" data-testid={`fact-inflight-unresolved-${a.bank_account_id}`}>
                    <Lock className="h-3.5 w-3.5 mt-[1px] shrink-0" />
                    <span>
                      {a.in_flight_verified === false
                        ? t('clinic.settlementAccounts.inFlightUnverified')
                        : t('clinic.settlementAccounts.inFlightUnattributed',
                          { count: a.in_flight_unattributed })}
                    </span>
                  </div>
                )}
              </div>
            );
          }
          return (
            <div
              key={a.bank_account_id}
              className="rounded-lg border border-slate-200 p-3 mb-3"
              data-testid={`settlement-account-${a.bank_account_id}`}
            >
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <span className="text-[13px] font-semibold text-slate-800" data-testid={`settlement-currency-${a.bank_account_id}`}>
                  {a.currency || '—'}
                </span>
                <span className="text-[12px] text-slate-500 font-mono">{a.iban_masked || '—'}</span>
                {a.is_primary && <Badge variant="secondary" className="text-[10px]">{t('clinic.settlementAccounts.primary')}</Badge>}
                {!a.is_active && <Badge variant="outline" className="text-[10px]">{t('clinic.settlementAccounts.inactive')}</Badge>}
              </div>
              <div className="grid gap-1.5 sm:grid-cols-2">
                <Fact ok testId={`fact-recorded-${a.bank_account_id}`}>
                  {t('clinic.settlementAccounts.bankRecorded')}
                </Fact>
                <Fact ok={a.bank_verification === 'verified'} testId={`fact-bank-verified-${a.bank_account_id}`}>
                  {a.bank_verification === 'verified'
                    ? t('clinic.settlementAccounts.bankVerified')
                    : t('clinic.settlementAccounts.bankPending')}
                </Fact>
                <Fact ok={a.psp_account_status === 'active'} testId={`fact-psp-${a.bank_account_id}`}>
                  {t(`clinic.settlementAccounts.${statusKey}`)}
                </Fact>
                <Fact ok={!!cap.eligible_for_charge} testId={`fact-charge-${a.bank_account_id}`}>
                  {cap.eligible_for_charge
                    ? t('clinic.settlementAccounts.chargeCapabilityGeneral', { currency: a.currency })
                    : t('clinic.settlementAccounts.chargeNotEligible', { currency: a.currency })}
                </Fact>
                <Fact ok={!!cap.eligible_for_settlement} testId={`fact-settlement-${a.bank_account_id}`}>
                  {cap.eligible_for_settlement
                    ? t('clinic.settlementAccounts.settlementCapabilityGeneral', { currency: a.currency })
                    : t('clinic.settlementAccounts.settlementNotEligible', { currency: a.currency })}
                </Fact>
                <Fact ok={!!a.psp?.can_receive_payouts} testId={`fact-payout-${a.bank_account_id}`}>
                  {a.psp?.can_receive_payouts
                    ? t('clinic.settlementAccounts.payoutEnabled')
                    : t('clinic.settlementAccounts.payoutNotEnabled')}
                </Fact>
                <Fact ok={!!a.bank_settlement_verified?.verified} testId={`fact-bank-settlement-${a.bank_account_id}`}>
                  {a.bank_settlement_verified?.verified
                    ? t('clinic.settlementAccounts.bankSettlementVerified', { currency: a.currency })
                    : t('clinic.settlementAccounts.bankSettlementNotVerified', { currency: a.currency })}
                </Fact>
                {a.psp?.settlement_currency && (
                  <Fact ok testId={`fact-settlement-currency-${a.bank_account_id}`}>
                    {t('clinic.settlementAccounts.settlesInCurrency',
                      { currency: a.psp.settlement_currency })}
                  </Fact>
                )}
                {/* CONVERTED SETTLEMENT — the provider is paid in the currency
                  their PSP account settles in, which may differ from what the
                  patient is charged. Stated plainly, with no promise of a rate,
                  a net amount or a date, and recorded as an explicit choice. */}
              {a.psp?.payment_account_id && a.psp?.settlement_currency
                && missingPairsFor(a).length > 0 && (
                <div
                  className="mt-2 rounded-md border border-slate-300/70 bg-slate-50 p-2.5 text-[12px] text-slate-700 sm:col-span-2"
                  data-testid={`converted-settlement-disclosure-${a.bank_account_id}`}
                >
                  <p>{t('clinic.settlementAccounts.convertedDisclosure',
                    { currency: a.psp.settlement_currency })}</p>
                  <p className="mt-1 font-medium" data-testid={`converted-settlement-scope-${a.bank_account_id}`}>
                    {t('clinic.settlementAccounts.convertedPairsScope',
                      { pairs: pairLabel(missingPairsFor(a)) })}
                  </p>
                  <button
                    type="button"
                    className="mt-2 rounded-full border border-slate-400 px-3 py-1 font-medium transition-colors hover:bg-slate-200 disabled:opacity-50"
                    disabled={busy === `acc-${a.bank_account_id}`}
                    onClick={() => acceptConverted(a)}
                    data-testid={`accept-converted-settlement-${a.bank_account_id}`}
                  >
                    {t('clinic.settlementAccounts.convertedAcceptAction',
                      { currency: a.psp.settlement_currency })}
                  </button>
                </div>
              )}
              {(a.psp?.converted_settlement_accepted_pairs || []).length > 0 && (() => {
                const accepted = a.psp.converted_settlement_accepted_pairs;
                // Pairs the provider accepted AND the PSP capability registry
                // has verified are USABLE — the screen used to tell every
                // provider that the conversion "also needs to be verified",
                // even when it already was, which read as a blocker.
                const pending = accepted.filter(p => p.capability_verified === false);
                const usable = accepted.filter(p => p.capability_verified !== false);
                return (
                  <p
                    className="mt-2 text-[12px] text-slate-600 sm:col-span-2"
                    data-testid={`converted-settlement-accepted-${a.bank_account_id}`}
                  >
                    {usable.length > 0 && (
                      <span data-testid={`converted-settlement-usable-${a.bank_account_id}`}>
                        {t('clinic.settlementAccounts.convertedUsable',
                          { currency: a.psp.settlement_currency })}
                        {' '}
                        {t('clinic.settlementAccounts.convertedPairsScope', {
                          pairs: pairLabel(usable),
                        })}
                      </span>
                    )}
                    {pending.length > 0 && (
                      <span data-testid={`converted-settlement-pending-${a.bank_account_id}`}>
                        {usable.length > 0 ? ' ' : null}
                        {t('clinic.settlementAccounts.convertedAccepted',
                          { currency: a.psp.settlement_currency })}
                        {' '}
                        {t('clinic.settlementAccounts.convertedPairsScope', {
                          pairs: pairLabel(pending),
                        })}
                      </span>
                    )}
                  </p>
                );
              })()}
              {a.psp_link_ambiguous && (
                  <div className="flex items-start gap-1.5 text-[12px] text-amber-700 sm:col-span-2" data-testid={`fact-link-ambiguous-${a.bank_account_id}`}>
                    <Lock className="h-3.5 w-3.5 mt-[1px] shrink-0" />
                    <span>{t('clinic.settlementAccounts.pspLinkAmbiguous')}</span>
                  </div>
                )}
                {a.in_flight_payments > 0 && (
                  <div className="flex items-start gap-1.5 text-[12px] text-amber-700" data-testid={`fact-inflight-${a.bank_account_id}`}>
                    <Lock className="h-3.5 w-3.5 mt-[1px] shrink-0" />
                    <span>{t('clinic.settlementAccounts.inFlight', { count: a.in_flight_payments })}</span>
                  </div>
                )}
                {/* Unknown is NOT one confirmed settling payment. */}
                {(a.in_flight_unattributed > 0 || a.in_flight_verified === false) && (
                  <div className="flex items-start gap-1.5 text-[12px] text-amber-700" data-testid={`fact-inflight-unresolved-${a.bank_account_id}`}>
                    <Lock className="h-3.5 w-3.5 mt-[1px] shrink-0" />
                    <span>
                      {a.in_flight_verified === false
                        ? t('clinic.settlementAccounts.inFlightUnverified')
                        : t('clinic.settlementAccounts.inFlightUnattributed',
                          { count: a.in_flight_unattributed })}
                    </span>
                  </div>
                )}
              </div>
              {/* The provider's last answer, kept visible after a reload — with
                  the classification the backend PERSISTED beside it, so a
                  system error is not read as a rejection after a refresh or a
                  new sign-in. Only for an account iyzico can settle into. */}
              {settles === true && (() => {
                const outstanding = (a.psp?.requirements_outstanding || []).map(String);
                const refusal = outstanding.find((r) => r.startsWith('provider_response_failed:'));
                if (!refusal) return null;
                const persistedClass = (outstanding
                  .find((r) => r.startsWith('provider_response_class:')) || '')
                  .slice('provider_response_class:'.length);
                const code = (outstanding
                  .find((r) => r.startsWith('provider_error_code:')) || '')
                  .slice('provider_error_code:'.length);
                // No persisted classification (an older record) is UNKNOWN —
                // never a permanent rejection.
                const titleKey = {
                  retryable_system_error: 'pspStateTemporaryFailure',
                  business_refusal: 'pspStatePspRejected',
                  unclassified_provider_answer: 'pspStateUncertain',
                }[persistedClass] || 'pspStateUncertain';
                const permanent = persistedClass === 'business_refusal';
                return (
                  <div
                    className={`mt-2 rounded-md border p-2.5 text-[12px] ${permanent
                      ? 'border-rose-300/70 bg-rose-50/70 text-rose-900'
                      : 'border-amber-300/70 bg-amber-50/70 text-amber-900'}`}
                    data-testid={`psp-last-refusal-${a.bank_account_id}`}
                    data-failure-class={persistedClass || 'unknown'}
                  >
                    <p className="font-medium">
                      {t(`clinic.settlementAccounts.${titleKey}`)}
                    </p>
                    <p data-testid={`psp-last-refusal-text-${a.bank_account_id}`}>
                      {refusal.slice('provider_response_failed:'.length)}
                    </p>
                    {code && (
                      <p
                        className="mt-1 font-mono text-[11px] opacity-80"
                        data-testid={`psp-last-refusal-code-${a.bank_account_id}`}
                      >
                        {t('clinic.settlementAccounts.pspProviderErrorCode', { code })}
                      </p>
                    )}
                    {!permanent && (
                      <p
                        className="mt-1"
                        data-testid={`psp-last-refusal-retryable-${a.bank_account_id}`}
                      >
                        {t('clinic.settlementAccounts.pspRegisterTemporaryFailure')}
                      </p>
                    )}
                  </div>
                );
              })()}
              {/* What the connect action would refuse RIGHT NOW — shown before
                  the owner clicks, from the backend's own shared check. */}
              {settles === true
                && (a.registration_prerequisites?.blockers || []).length > 0
                && a.psp_account_status !== 'active' && (
                <div
                  className="mt-2 rounded-md border border-slate-300/70 bg-slate-50 p-2.5 text-[12px] text-slate-700"
                  data-testid={`psp-prerequisites-${a.bank_account_id}`}
                >
                  <p className="font-medium">{t('clinic.settlementAccounts.pspPrerequisites')}</p>
                  <ul className="list-disc pl-4">
                    {a.registration_prerequisites.blockers.map((b) => (
                      <li key={b} data-testid={`psp-prerequisite-${b}`}>
                        {t(`clinic.settlementAccounts.pspBlocker.${b.split(':')[0]}`,
                          { defaultValue: b })}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {blocked[a.bank_account_id] && (
                <div
                  className="mt-2 rounded-md border border-amber-300/70 bg-amber-50/70 p-2.5 text-[12px] text-amber-900"
                  data-testid={`psp-register-blocked-${a.bank_account_id}`}
                >
                  <div className="flex items-start gap-1.5">
                    <AlertTriangle className="h-3.5 w-3.5 mt-[1px] shrink-0" />
                    <div className="space-y-1">
                      <p className="font-medium" data-testid={`psp-register-state-${a.bank_account_id}`}>
                        {t(`clinic.settlementAccounts.${
                          STATE_TITLES[blocked[a.bank_account_id].state]
                          || 'pspStateLocalValidation'}`)}
                      </p>
                      {/* Truthful dispatch fact: whether a request actually
                          left TreatX, stated independently of the reason. */}
                      <p data-testid={`psp-register-dispatch-${a.bank_account_id}`}>
                        {t(blocked[a.bank_account_id].external_write_sent
                          ? 'clinic.settlementAccounts.pspRequestSent'
                          : 'clinic.settlementAccounts.pspRequestNotSent')}
                      </p>
                      <p>{blocked[a.bank_account_id].message
                        || t('clinic.settlementAccounts.pspRegisterNotReady')}</p>
                      {/* A constraint the provider itself stated, explained in
                          the owner's language, with the next step. */}
                      {blocked[a.bank_account_id].provider_constraint && (
                        <p
                          className="font-medium"
                          data-testid={`psp-register-constraint-${a.bank_account_id}`}
                        >
                          {t(`clinic.settlementAccounts.pspConstraint.${
                            blocked[a.bank_account_id].provider_constraint}`,
                          { defaultValue: blocked[a.bank_account_id].provider_constraint })}
                        </p>
                      )}
                      {/* Provider evidence ONLY when the provider answered. */}
                      {['PSP_REJECTED', 'PSP_TEMPORARY_FAILURE'].includes(
                        blocked[a.bank_account_id].state)
                        && blocked[a.bank_account_id].provider_error_code && (
                        <p
                          className="font-mono text-[11px]"
                          data-testid={`psp-register-error-code-${a.bank_account_id}`}
                        >
                          {t('clinic.settlementAccounts.pspErrorCode', {
                            code: blocked[a.bank_account_id].provider_error_code,
                            group: blocked[a.bank_account_id].provider_error_group || '—',
                          })}
                        </p>
                      )}
                      {(blocked[a.bank_account_id].blockers || []).length > 0 && (
                        <ul className="list-disc pl-4">
                          {blocked[a.bank_account_id].blockers.map((b) => (
                            // Raw codes told the owner nothing. Each blocker is
                            // rendered as an actionable sentence; an unknown
                            // code still shows itself rather than vanishing.
                            <li key={b} data-testid={`psp-register-blocker-${b}`}>
                              {t(`clinic.settlementAccounts.pspBlocker.${b.split(':')[0]}`,
                                { defaultValue: b })}
                            </li>
                          ))}
                        </ul>
                      )}
                      {blocked[a.bank_account_id].state === 'PSP_REJECTED'
                        && blocked[a.bank_account_id].psp_evidence && (
                        <p data-testid={`psp-register-evidence-${a.bank_account_id}`}>
                          {t('clinic.settlementAccounts.pspEvidence', {
                            iban: blocked[a.bank_account_id].psp_evidence.iyzico_iban_masked || '—',
                            currency: blocked[a.bank_account_id].psp_evidence.iyzico_currency || '—',
                          })}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}
              {a.is_active && (
                <div className="mt-2 flex flex-wrap justify-end gap-2">
                  {/* OWNER DECISION 2026-10 — for iyzico the provider settles in
                      TRY. Offering to register a non-TRY bank account as an
                      iyzico settlement destination sent the owner down a
                      journey iyzico cannot complete, and then showed its
                      refusal as if the provider had to fix something. The bank
                      record itself, its history and every other PSP are
                      untouched: only THIS screen's connect action is scoped to
                      the currencies iyzico can actually settle. With NO
                      capability evidence nothing is offered either — an
                      unknown capability is not a supported one. */}
                  {a.psp_account_status !== 'active' && (
                    settles === true ? (
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={busy === a.bank_account_id}
                        onClick={() => registerWithPsp(a.bank_account_id)}
                        data-testid={`settlement-psp-register-${a.bank_account_id}`}
                      >
                        {t('clinic.settlementAccounts.pspRegisterAction', { currency: a.currency })}
                      </Button>
                    ) : (
                      <p
                        className="text-[12px] text-slate-600 text-left mr-auto"
                        data-testid={`settlement-psp-capability-unknown-${a.bank_account_id}`}
                      >
                        {t('clinic.settlementAccounts.pspCapabilityUnknown')}
                      </p>
                    )
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={busy === a.bank_account_id || deactivationBlocked(a)}
                    onClick={() => deactivate(a.bank_account_id)}
                    data-testid={`settlement-deactivate-${a.bank_account_id}`}
                  >
                    {t('clinic.settlementAccounts.deactivate')}
                  </Button>
                </div>
              )}
            </div>
          );
        })}

        {!loading && !failed && matrix && (
          <div className="mt-4 rounded-lg bg-slate-50 border border-slate-200 p-3" data-testid="currency-capability-matrix">
            <div className="flex items-center gap-1.5 mb-2">
              <AlertTriangle className="h-3.5 w-3.5 text-slate-500" />
              <h4 className="text-[12px] font-semibold text-slate-800">
                {t('clinic.settlementAccounts.capabilityHeading')}
              </h4>
            </div>
            <div className="grid gap-1 sm:grid-cols-2">
              {(matrix.charge || []).map((c) => (
                <div key={`charge-${c.currency}`} className="text-[12px] flex items-center gap-2" data-testid={`capability-charge-${c.currency}`}>
                  <Badge variant={c.selectable ? 'secondary' : 'outline'} className="text-[10px]">
                    {c.selectable ? t('clinic.settlementAccounts.confirmed') : t('clinic.settlementAccounts.notConfirmed')}
                  </Badge>
                  <span className="text-slate-600">{t('clinic.settlementAccounts.chargeEligible', { currency: c.currency })}</span>
                </div>
              ))}
              {(matrix.settlement || []).map((c) => (
                <div key={`settle-${c.currency}`} className="text-[12px] flex items-center gap-2" data-testid={`capability-settlement-${c.currency}`}>
                  <Badge variant={c.selectable ? 'secondary' : 'outline'} className="text-[10px]">
                    {c.selectable ? t('clinic.settlementAccounts.confirmed') : t('clinic.settlementAccounts.notConfirmed')}
                  </Badge>
                  <span className="text-slate-600">{t('clinic.settlementAccounts.settlementEligible', { currency: c.currency })}</span>
                </div>
              ))}
            </div>
            <p className="text-[11px] text-slate-500 mt-2">{t('clinic.settlementAccounts.capabilityNote')}</p>
            <p className="text-[11px] text-slate-500 mt-1">{t('clinic.settlementAccounts.addAnother')}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default SettlementAccountsCard;
